// User Service
// Create and manage user accounts (Auth + Firestore + Lender)

import { collection, doc, setDoc, Timestamp } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { createLender } from './lenderService';

export interface CreateInvestorData {
  // Auth
  email: string;
  password: string;
  
  // Personal info
  fullName: string;
  phone: string;
  address: string;
  
  // Bank details
  bankName: string;
  accountName: string;
  accountNumber: string;
  bankCode?: string;
  branchCode?: string;
}

/**
 * Create complete investor account (Auth + User + Lender)
 * This automates the 3-step manual process
 */
export const createInvestorAccount = async (
  investorData: CreateInvestorData
): Promise<{ userId: string; lenderId: string }> => {
  try {
    // Store current user to re-login after
    const currentUser = auth.currentUser;
    
    // Step 1: Create Firebase Authentication account
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      investorData.email,
      investorData.password
    );
    
    const userId = userCredential.user.uid;
    
    // Step 2: Create Lender profile (do this BEFORE signing out or Firestore denies access)
    const lenderData = {
      fullName: investorData.fullName,
      email: investorData.email,
      phone: investorData.phone,
      address: investorData.address,
      totalInvested: 0, // Will be updated when series are created
      bankDetails: {
        bankName: investorData.bankName,
        accountName: investorData.accountName,
        accountNumber: investorData.accountNumber,
        bankCode: investorData.bankCode || '',
        branchCode: investorData.branchCode || '',
      },
      status: 'active' as const,
      kycStatus: 'pending' as const,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    const lenderId = await createLender(lenderData);
    
    // Step 3: Create Firestore user document (linked to lender)
    await setDoc(doc(db, 'users', userId), {
      email: investorData.email,
      role: 'investor',
      fullName: investorData.fullName,
      lenderId: lenderId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    
    console.log(`✅ Investor account created: ${investorData.email}`);
    console.log(`  User ID: ${userId}`);
    console.log(`  Lender ID: ${lenderId}`);
    
    return { userId, lenderId };
  } catch (error: any) {
    console.error('Error creating investor account:', error);
    
    // Provide user-friendly error messages
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('This email is already registered. Please use a different email.');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address format.');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('Password is too weak. Please use a stronger password (min 6 characters).');
    } else {
      throw new Error(`Failed to create investor account: ${error.message}`);
    }
  }
};

/**
 * Create admin account (Auth + User only, no lender)
 */
export const createAdminAccount = async (
  email: string,
  password: string,
  fullName: string
): Promise<string> => {
  try {
    // Create Firebase Auth account
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;
    
    // Create Firestore user document
    await setDoc(doc(db, 'users', userId), {
      email,
      role: 'admin',
      fullName,
      lenderId: null, // Admins don't have lender profiles
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    
    console.log(`✅ Admin account created: ${email}`);
    return userId;
  } catch (error: any) {
    console.error('Error creating admin account:', error);
    
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('This email is already registered.');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address format.');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('Password too weak (min 6 characters).');
    } else {
      throw new Error(`Failed to create admin account: ${error.message}`);
    }
  }
};

/**
 * Resend password reset email
 */
export const sendPasswordResetEmail = async (email: string): Promise<void> => {
  const { sendPasswordResetEmail: firebaseSendPasswordReset } = await import('firebase/auth');
  
  try {
    await firebaseSendPasswordReset(auth, email);
    console.log(`Password reset email sent to: ${email}`);
  } catch (error: any) {
    console.error('Error sending password reset email:', error);
    throw new Error('Failed to send password reset email');
  }
};
